import {
    buildChatHeaders,
    getAiChatConfig,
} from "../../lib/aiChatConfig.js";
import {
    SELF_CHECK_FEEDBACK_SYSTEM_PROMPT,
    buildSelfCheckFeedbackUserPrompt,
    parseSelfCheckFeedbackResponse,
} from '../utils/fillGapsSelfCheckAi.js';
import {
    buildFillGapsAiSystemPrompt,
    buildFillGapsAiUserPrompt,
    parseFillGapsAiFeedbackResponse,
} from '../utils/fillGapsAi.js';
import {
    MAGIC_FILL_SYSTEM_PROMPT,
    buildMagicFillUserPrompt,
} from '../utils/magicFillWords.js';
import {
    RAW_TEXT_DEFAULT_MODEL,
    assembleRawTextSystemPrompt,
    stripAiMarkersWrapper,
} from '../utils/rawTextToMarkersAi.js';
import {
    SUGGEST_BLOCK_TYPES_SYSTEM_PROMPT,
    GENERATE_BLOCK_EXERCISE_SYSTEM_PROMPT,
    buildSuggestBlockTypesUserPrompt,
    buildGenerateBlockExerciseUserPrompt,
    parseSuggestedTypes,
    parseGeneratedExercise,
} from '../utils/aiBlockExercises.js';

// Server-side proxy (recommended) so the browser never ships API keys.
// Works on Vercel (`/api/ai/translate`) and Netlify (redirects to function).
const AI_TRANSLATE_URL =
    import.meta.env.VITE_AI_TRANSLATE_URL || "/api/ai/translate";

const clientAi = getAiChatConfig(import.meta.env);

async function callAi(systemPrompt, userPrompt, { targetLang, model, rawTextToMarkers } = {}) {
    // 1) Server proxy: browser sends text + prompts; hosting calls Timeweb/OpenRouter with AI_API_KEY.
    try {
        const proxyBody = { text: userPrompt, systemPrompt };
        if (targetLang) proxyBody.targetLang = targetLang;
        if (model) proxyBody.model = model;
        if (rawTextToMarkers) proxyBody.rawTextToMarkers = true;

        const proxyResponse = await fetch(AI_TRANSLATE_URL, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(proxyBody),
        });

        const proxyData = await proxyResponse.json();
        if (!proxyResponse.ok || proxyData?.error) {
            throw new Error(proxyData?.error || `AI Proxy Error (${proxyResponse.status})`);
        }

        if (proxyData?.content) return proxyData.content.trim();
    } catch (proxyErr) {
        console.warn("AI proxy unavailable, falling back to direct API call.", proxyErr);
    }

    // 2) Fallback: direct OpenAI-compatible API call (local dev only).
    if (!clientAi.apiKey) {
        throw new Error(
            "AI proxy is not available, and no client AI API key is set. Configure server env AI_API_KEY (recommended) or VITE_AI_API_KEY for local dev only."
        );
    }

    const response = await fetch(clientAi.url, {
        method: "POST",
        headers: buildChatHeaders(clientAi),
        body: JSON.stringify({
            "model": model || clientAi.model,
            "messages": [
                { "role": "system", "content": systemPrompt },
                { "role": "user", "content": userPrompt }
            ],
        })
    });

    const data = await response.json();
    if (data.error) {
        throw new Error(data.error.message || "AI Error");
    }

    return data.choices[0].message.content.trim();
}

export const DEFAULT_PROMPTS = {
    maxLength: "You are an editor. Shorten the following text to at most {words} words while preserving the key meaning and all markdown formatting. Do not add any explanations, just the shortened text.",
    adaptCefr: "Rewrite the following text at CEFR level {level}. Preserve all markdown formatting. Do not add any explanations, just the rewritten text.",
    comprehension: "Based on the following text, generate 5 comprehension questions. Each question should test understanding of the text. Format each question on a new line starting with a number (1. 2. etc.). Do not include answers.",
    discussion: "Based on the following text, generate 5 discussion questions suitable for a language classroom. Questions should encourage speaking and critical thinking. Format each question on a new line starting with a number (1. 2. etc.).",
    cloze: "Replace every {nth} word in the text with '_____'. Count words from 1, and replace word number {nth}, then word number {nth}×2, then {nth}×3, {nth}×4, and continue this pattern. Return only the modified text with blanks.",
    trueFalse: "Based on the following text, generate 5 true/false statements. Mix true and false statements. Format each statement on a new line starting with a number (1. 2. etc.). Do not include answers.",
    wordFormation: `I will provide you with a text. Your task is to convert this text into an ЕГЭ-style word formation exercise.
Follow these rules:
1. Identify 5–7 places in the text where a derived word (e.g., noun from verb, adverb from adjective, adjective from noun, etc.) can be used instead of the base word.
2. In the text, replace those words with the following code: [initial base word/the word from the text].
3. Focus on typical transformations:
   - Adjective ↔ Noun (e.g., different → difference)
   - Verb ↔ Noun (e.g., decide → decision)
   - Adjective ↔ Adverb (e.g., quick → quickly)
   - Negative prefixes (un-, in-, im-, ir-, dis-, mis-)
   - Suffixes like -tion, -ment, -ence, -ance, -ity, -ness, -ous, -al, -ful, -less, -ly, etc.
4. Try to include different types of transformations from above.
5. Do not change the original meaning or order of the text.
6. If the text doesn't have enough derived words, you can paraphrase some sentences to include them.

Return only the modified text with gap codes embedded. No explanations or answer key.`,
    grammarTask: `I will provide you with a text. Your task is to convert this text into an ЕГЭ-style grammar task.
Follow these rules:
1. Identify 5–7 places in the text where a grammatical change of a base word is required, such as:
   - Verb tense
   - Passive voice
   - Conditionals
   - Comparative / superlative degrees
   - Plural forms
   - Personal, object, possessive, or reflexive pronouns
   - Modal verbs in past (can → could, must → had to)
   - Gerund or infinitive after certain verbs (enjoy swim → enjoying swimming)
   or other grammatical changes
2. In the text, replace the required grammatical form with the following code: [initial base word/the word from the text].
3. Do not use derivational word formation here (e.g., changing noun to adjective). Only grammatical inflections and structural changes.
4. Try to include different types of transformations from above.
5. Do not change the original meaning or order of the text.
6. If the text doesn't have enough grammatical forms, you can paraphrase some sentences to include them.

Return only the modified text with gap codes embedded. No explanations or answer key.`,
    vocabularyMc: `I will provide you with a text. Your task is to convert this text into an ЕГЭ-style vocabulary multiple-choice task.
Follow these rules:
1. Identify 6–8 places in the text where vocabulary choice is non-trivial. At each such place, a student must choose between four similar or easily confused words based on:
   - Meaning (e.g., say / tell / speak / talk)
   - Collocations (e.g., make a decision / do a decision ✗)
   - Phrasal verbs (e.g., give up / give in / give away / give out)
   - Word families with different nuances (e.g., famous / popular / well-known / notorious)
   - Prepositions fixed to verbs/nouns/adjectives (e.g., depend on / rely on / insist on)
   - Similar looking words (e.g. resign / refrain / recall)
   - False friends (e.g., magazine / shop / store / journal)
   - L1 interference (e.g., «learn» vs. «study»)
   - Confusable pairs (e.g., borrow / lend, rise / raise, affect / effect)
   - Register differences (formal vs. informal)
2. Replace the chosen word in the text with the following code: [option1/option2*/option3/option4]. The correct option is marked with a * symbol. Only this one option is correct, i.e. fits by meaning and grammar in the sentence — other options must be plausible but wrong in this sentence.
3. If the text doesn't have enough such words, you can paraphrase some sentences to include them.

Return only the modified text with gap codes embedded. No explanations or answer key.`,
    rolePlay: `I will provide you with a text. Your task is to convert it into a role-playing speaking activity for the English classroom.
Follow these rules:
1. Extract the main theme, setting, and conflict or decision from the text.
2. Design a realistic situation where two students (or student and teacher) act out roles connected to the text.
3. Provide:
   - A short Situation (2–4 sentences) setting the scene
   - Role A and Role B: who each person is and what they want
   - 4–6 bullet prompts or mini-tasks each speaker should cover during the role-play
   - 6–10 useful phrases or chunks students may use (taken from or inspired by the text)
4. Keep language at roughly the same level as the source text. Do not copy long passages verbatim.
5. If the text is too thin for a role-play, invent a plausible extension that stays on topic.

Return only the activity (headings: Situation, Role A, Role B, Tasks, Useful phrases). No meta-commentary.`,
    parseComment: "Parse the following text line by line. Each line contains an English word and possibly its Russian translation. If Russian is missing, add it. Format each pair as 'English word - Russian translation' on a new line. Return only the formatted list, no explanations.",
    magicFillWords: MAGIC_FILL_SYSTEM_PROMPT,
};

export const aiService = {
    async translate(text, targetLang = 'ru') {
        const systemPrompt = "You are a professional translator specialized in language learning materials. You preserve formatting exactly.";
        const userPrompt = targetLang === 'ru' 
            ? `Translate the following English text to Russian. Preserve all markdown formatting (like **bold**, _italic_, ||blur||, etc.) and images. Do not add any explanations, just the translation.\n\nText: ${text}`
            : `Translate the following Russian text to English. Preserve all markdown formatting (like **bold**, _italic_, ||blur||, etc.) and images. Do not add any explanations, just the translation.\n\nText: ${text}`;

        try {
            return await callAi(systemPrompt, userPrompt, { targetLang });
        } catch (error) {
            console.error("AI Translation Error:", error);
            throw error;
        }
    },

    async reworkText(text, action, params = {}, customPrompts = {}) {
        const prompts = { ...DEFAULT_PROMPTS, ...customPrompts };
        let systemPrompt = prompts[action] || '';
        let userPrompt = text;

        // Replace placeholders in the system prompt
        if (params.words && action === 'maxLength') {
            systemPrompt = systemPrompt.replace('{words}', params.words);
        }
        if (params.level && action === 'adaptCefr') {
            systemPrompt = systemPrompt.replace('{level}', params.level);
        }
        if (params.nth && action === 'cloze') {
            systemPrompt = systemPrompt.replace('{nth}', params.nth);
            console.log('[DEBUG] Cloze system prompt:', systemPrompt);
        }

        try {
            return await callAi(systemPrompt, userPrompt);
        } catch (error) {
            console.error("AI Rework Error:", error);
            throw error;
        }
    },

    async parseComment(text, customPrompts = {}) {
        const prompts = { ...DEFAULT_PROMPTS, ...customPrompts };
        const systemPrompt = prompts.parseComment || '';
        const userPrompt = text;

        try {
            return await callAi(systemPrompt, userPrompt);
        } catch (error) {
            console.error("AI Parse Comment Error:", error);
            throw error;
        }
    },

    async generateSelfCheckFeedback(items) {
        const userPrompt = buildSelfCheckFeedbackUserPrompt(items);
        try {
            const content = await callAi(SELF_CHECK_FEEDBACK_SYSTEM_PROMPT, userPrompt);
            return parseSelfCheckFeedbackResponse(content, items.length);
        } catch (error) {
            console.error('AI Self-Check Feedback Error:', error);
            throw error;
        }
    },

    /**
     * Student-facing Fill Gaps (AI) check — returns per-gap feedback + correct flags.
     * @param {Array} items
     * @param {string} [course] exercise.course for feedback language (L1–L4)
     * @param {{ instruction?: string, sectionBody?: string }} [opts]
     */
    async generateFillGapsAiFeedback(items, course, opts = {}) {
        const systemPrompt = buildFillGapsAiSystemPrompt(course, { instruction: opts.instruction });
        const userPrompt = buildFillGapsAiUserPrompt(items, {
            instruction: opts.instruction,
            sectionBody: opts.sectionBody,
        });
        try {
            const content = await callAi(systemPrompt, userPrompt);
            return parseFillGapsAiFeedbackResponse(content, items.length);
        } catch (error) {
            console.error('AI Fill Gaps Feedback Error:', error);
            throw error;
        }
    },

    /**
     * Batch Magic Fill for Global Words empty fields.
     * @param {Array<{id:string, english:string, ctx:object, fill:string[]}>} items
     * @param {{ magicFillWords?: string }} [customPrompts]
     */
    async magicFillWords(items, customPrompts = {}) {
        const prompts = { ...DEFAULT_PROMPTS, ...customPrompts };
        const systemPrompt = prompts.magicFillWords || MAGIC_FILL_SYSTEM_PROMPT;
        const userPrompt = buildMagicFillUserPrompt(items);
        try {
            return await callAi(systemPrompt, userPrompt);
        } catch (error) {
            console.error('AI Magic Fill Error:', error);
            throw error;
        }
    },

    /**
     * Convert pasted raw text into exercise marker body for the given type.
     * @param {string} rawText
     * @param {string} exerciseType
     * @param {{ systemPrompt?: string, promptOverride?: string, customNotes?: string, model?: string }} [opts]
     */
    async convertRawTextToMarkers(rawText, exerciseType, opts = {}) {
        const text = String(rawText || '').trim();
        if (!text) throw new Error('Paste some text first.');
        const systemPrompt = (opts.systemPrompt && String(opts.systemPrompt).trim())
            || assembleRawTextSystemPrompt(exerciseType, {
                promptOverride: opts.promptOverride,
                customNotes: opts.customNotes,
            });
        const userPrompt = `Convert the following raw text into the exercise body markers for type "${exerciseType || 'unknown'}".\n\n${text}`;
        try {
            const content = await callAi(systemPrompt, userPrompt, {
                model: opts.model || RAW_TEXT_DEFAULT_MODEL,
                rawTextToMarkers: true,
            });
            return stripAiMarkersWrapper(content);
        } catch (error) {
            console.error('AI Raw Text → Markers Error:', error);
            throw error;
        }
    },

    /**
     * Suggest exercise type sequence for an AI block.
     * @param {object} ctx — from buildAiBlockContext
     * @param {{ existingTypes?: string[], emptyList?: boolean, model?: string }} [opts]
     * @returns {Promise<string[]>}
     */
    async suggestBlockExerciseTypes(ctx, opts = {}) {
        const userPrompt = buildSuggestBlockTypesUserPrompt(ctx, {
            existingTypes: opts.existingTypes || [],
            emptyList: Boolean(opts.emptyList),
        });
        try {
            const content = await callAi(SUGGEST_BLOCK_TYPES_SYSTEM_PROMPT, userPrompt, {
                model: opts.model || RAW_TEXT_DEFAULT_MODEL,
            });
            return parseSuggestedTypes(content);
        } catch (error) {
            console.error('AI Suggest Block Types Error:', error);
            throw error;
        }
    },

    /**
     * Generate one exercise draft for a block (title / instruction / body).
     * @param {object} ctx — from buildAiBlockContext
     * @param {string} exerciseType
     * @param {{ model?: string, notes?: string }} [opts]
     */
    async generateBlockExercise(ctx, exerciseType, opts = {}) {
        if (!exerciseType) throw new Error('Exercise type is required');
        const userPrompt = buildGenerateBlockExerciseUserPrompt(ctx, exerciseType, opts.notes || '');
        try {
            const content = await callAi(GENERATE_BLOCK_EXERCISE_SYSTEM_PROMPT, userPrompt, {
                model: opts.model || RAW_TEXT_DEFAULT_MODEL,
            });
            return parseGeneratedExercise(content, exerciseType);
        } catch (error) {
            console.error('AI Generate Block Exercise Error:', error);
            throw error;
        }
    },
};

export default aiService;
